import { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { journeyId } from './observability/journey';
import { hasPreviewPass } from './preview/previewAccess';

// ── React plugin (provides component-level tracking & React Router integration) ──
export const reactPlugin = new ReactPlugin();

// ── Application Insights instance ──
let appInsights: ApplicationInsights | null = null;

/**
 * Initialise Application Insights.
 * Called only AFTER the user has accepted cookie consent.
 * Safe to call multiple times — subsequent calls are no-ops.
 */
export const initTelemetry = (): ApplicationInsights | null => {
	if (appInsights) return appInsights; // already initialised

	const connectionString = process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING;
	if (!connectionString) {
		console.debug('[Telemetry] No connection string configured — telemetry disabled.');
		return null;
	}

	appInsights = new ApplicationInsights({
		config: {
			connectionString,
			extensions: [reactPlugin],
			enableAutoRouteTracking: true,    // track page views on route changes
			disableFetchTracking: false,      // track outgoing fetch() calls
			enableCorsCorrelation: true,      // correlate front-end ↔ back-end
			enableRequestHeaderTracking: false, // don't capture request headers (PII risk)
			enableResponseHeaderTracking: false,
			disableCookiesUsage: false,       // allow anonymous session cookies
			autoTrackPageVisitTime: true,     // track how long users stay on each page
		},
	});

	appInsights.loadAppInsights();

	// ── PII scrubber — runs on every telemetry item before it's sent ──
	appInsights.addTelemetryInitializer((item: ITelemetryItem) => {
		// Remove user-identifiable IP (extra safety on top of Azure's default anonymisation)
		if (item.tags) {
			item.tags['ai.location.ip'] = '0.0.0.0';
		}

		// Strip any accidental email / name from custom properties
		const props = item.data as Record<string, any> | undefined;
		if (props) {
			const sensitivePattern = /^(email|name|firstName|lastName|phone|address)$/i;
			for (const key of Object.keys(props)) {
				if (sensitivePattern.test(key)) {
					delete props[key];
				}
			}
		}

		// Link browser telemetry to the same journey as the API calls.
		item.data = { ...(item.data || {}), 'journey.id': journeyId };
		// Pre-launch preview visitors (invited testers) — filter them out of funnel numbers.
		if (hasPreviewPass()) item.data['preview'] = 'true';

		return true; // keep the item
	});

	console.debug('[Telemetry] Application Insights initialised.');

	// ── Global fetch interceptor — track all API failures automatically ──
	const originalFetch = window.fetch.bind(window);
	window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
		const url = typeof args[0] === 'string' ? args[0] : args[0] instanceof Request ? args[0].url : String(args[0]);
		const method = (typeof args[1]?.method === 'string' ? args[1].method : 'GET').toUpperCase();

		try {
			const response = await originalFetch(...args);

			if (!response.ok) {
				// Clone so the body is still consumable by the caller
				const clone = response.clone();
				let errorBody = '';
				try { errorBody = (await clone.text()).substring(0, 500); } catch { /* ignore */ }

				trackEvent('ApiError', {
					url: url.replace(/https?:\/\/[^/]+/, ''),  // strip origin to avoid PII in full URLs
					method,
					status: response.status,
					statusText: response.statusText,
					errorBody,
				});
			}

			return response;
		} catch (networkError: any) {
			// Network-level failure (DNS, timeout, CORS block, offline)
			trackException(networkError instanceof Error ? networkError : new Error(String(networkError)), {
				url: url.replace(/https?:\/\/[^/]+/, ''),
				method,
				kind: 'NetworkError',
			});
			throw networkError;
		}
	};

	return appInsights;
};

/**
 * Get the current Application Insights instance (or null if not yet initialised).
 */
export const getAppInsights = (): ApplicationInsights | null => appInsights;

// ─── Convenience helpers for tracking business events ───────────────────────

export const trackEvent = (name: string, properties?: Record<string, string | number | boolean>) => {
	appInsights?.trackEvent({ name }, properties);
};

export const trackPageView = (name: string, uri?: string) => {
	appInsights?.trackPageView({ name, uri });
};

export const trackException = (error: Error, properties?: Record<string, string>) => {
	appInsights?.trackException({ exception: error }, properties);
};

// ─── Pre-defined business events ────────────────────────────────────────────

export const telemetryEvents = {
	/** User viewed a product detail page */
	productViewed: (productType: string, productId: string) =>
		trackEvent('ProductViewed', { productType, productId }),

	/** User selected an art style */
	styleSelected: (style: string) =>
		trackEvent('StyleSelected', { style }),

	/** User added an item to cart */
	addedToCart: (productType: string, price: number) =>
		trackEvent('AddedToCart', { productType, price }),

	/** User started the checkout flow */
	checkoutStarted: (itemCount: number, cartTotal: number) =>
		trackEvent('CheckoutStarted', { itemCount, cartTotal }),

	/** Order was placed successfully */
	orderPlaced: (orderRef: string, itemCount: number, total: number) =>
		trackEvent('OrderPlaced', { orderRef, itemCount, total }),

	/** Payment confirmation page was shown */
	paymentConfirmed: (orderRef: string) =>
		trackEvent('PaymentConfirmed', { orderRef }),

	/** User generated an avatar */
	avatarGenerated: (style: string, gender: string) =>
		trackEvent('AvatarGenerated', { style, gender }),

	/** User signed in */
	userSignedIn: () =>
		trackEvent('UserSignedIn'),

	/** User signed up */
	userSignedUp: () =>
		trackEvent('UserSignedUp'),

	/** API call failed (for manual use in catch blocks with extra context) */
	apiError: (endpoint: string, method: string, status: number, message?: string) =>
		trackEvent('ApiError', { url: endpoint, method, status, statusText: message || '' }),

	/** Customer requested a redo of a book page during the pre-print review */
	bookRedoRequested: (orderRef: string, itemIndex: number, pageNumber: number) =>
		trackEvent('BookRedoRequested', { orderRef, itemIndex, pageNumber }),

	/** Customer reverted a page to a previous version during review */
	bookVersionReverted: (orderRef: string, itemIndex: number, pageNumber: number) =>
		trackEvent('BookVersionReverted', { orderRef, itemIndex, pageNumber }),

	/** Customer approved a book (finalized, ready for print/digital delivery) */
	bookApproved: (orderRef: string, itemIndex: number) =>
		trackEvent('BookApproved', { orderRef, itemIndex }),

	/** Customer contacted support from the review page about a page they're unhappy with */
	reviewContactSubmitted: (orderRef: string) =>
		trackEvent('ReviewContactSubmitted', { orderRef }),

	/** Customer's finished book entered reading mode (cover-to-cover reader) */
	bookReadingStarted: (orderRef: string, itemIndex: number) =>
		trackEvent('BookReadingStarted', { orderRef, itemIndex }),

	/** Customer opened a book from their shelf ("My Orders") */
	shelfBookOpened: (orderId: string) =>
		trackEvent('ShelfBookOpened', { orderId }),

	/** Customer requested a password reset email */
	passwordResetRequested: () =>
		trackEvent('PasswordResetRequested'),

	/** Customer successfully reset their password */
	passwordResetCompleted: () =>
		trackEvent('PasswordResetCompleted'),

	/** Customer started purchasing a generation-credit pack (Stripe checkout opened) */
	creditPackPurchaseStarted: (packId: string, credits: number, price: number) =>
		trackEvent('CreditPackPurchaseStarted', { packId, credits, price }),

	/** Generation credits were successfully added to the customer's account */
	creditsPurchased: (totalCredits: number) =>
		trackEvent('CreditsPurchased', { totalCredits }),

	/** Customer downloaded their personal data (GDPR Art. 15) */
	dataExportRequested: () =>
		trackEvent('DataExportRequested'),

	/** Customer submitted the account deletion confirmation (GDPR Art. 17) */
	accountDeletionRequested: () =>
		trackEvent('AccountDeletionRequested'),

	/** Customer's account was successfully deleted */
	accountDeleted: () =>
		trackEvent('AccountDeleted'),

	/** Visitor signed up for early access on the pre-launch coming-soon page */
	earlyAccessSignedUp: () =>
		trackEvent('EarlyAccessSignedUp'),

	/** Shopper changed their delivery country/region */
	countryChanged: (country: string) =>
		trackEvent('CountryChanged', { country }),
};
